import { createTheme } from '@mui/material/styles'

const colours = {
  lightGrey: '#fafafa',
  brick: '#AF231C',
  white: '#ffffff',
  fadedGrey: '#595959',
}

// New Variant Type Definitions
declare module '@mui/material/styles' {
  interface TypographyVariants {
    anchor: React.CSSProperties
    visuallyHidden: React.CSSProperties
  }

  interface ChipVariants {
    subtle: React.CSSProperties
    inverted: React.CSSProperties
  }

  // allow configuration using `createTheme`
  interface TypographyVariantsOptions {
    anchor?: React.CSSProperties
    visuallyHidden?: React.CSSProperties
  }

  interface ChipVariantsOptions {
    subtle?: React.CSSProperties
    inverted?: React.CSSProperties
  }
}

// Update the Typography's variant prop options
declare module '@mui/material/Typography' {
  interface TypographyPropsVariantOverrides {
    anchor: true
    visuallyHidden: true
  }
}

declare module '@mui/material/Chip' {
  interface ChipPropsVariantOverrides {
    subtle: true
    inverted: true
  }
}

const font = 'Montserrat, Open Sans'

const theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 905,
      lg: 1240,
      xl: 1344,
    },
  },
  palette: {
    primary: {
      main: colours.brick,
      contrastText: colours.white,
    },
    info: { main: colours.fadedGrey },
  },
  typography: {
    fontFamily: font,
    allVariants: {
      color: '#1F1F1F',
      marginBlock: 0,
    },
    h2: {
      lineHeight: 32 / 24,
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h3: {
      // sae as h2 but doesn't grow with screensize
      lineHeight: 32 / 24,
      fontSize: '1.5rem',
      fontWeight: 500,
    },
    h4: {
      fontSize: '1rem',
      fontWeight: 600,
      lineHeight: 1.5,
    },
    subtitle1: {
      fontSize: '1.125rem',
      fontWeight: 400,
      lineHeight: 28 / 18,
    },
    body1: {
      fontSize: '1rem',
      fontWeight: 400,
      lineHeight: 1.5,
    },
    button: {
      fontSize: '1rem',
      fontWeight: 600,
    },
  },
  components: {
    MuiButtonBase: { defaultProps: { disableRipple: true } },
    MuiButton: { styleOverrides: { root: { outline: 'revert' } } }, // Accessible focus
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: '0.25rem',
          lineHeight: 1.5,
          gap: '0.25rem', // spacing between icon and label
          cursor: 'inherit',
          height: 'auto',
          fontWeight: 500,
          transition: 'none',
        },
        icon: {
          fontSize: '1em', // large chips should have larger icons
          margin: 0,
        },
        iconSmall: {
          fontSize: '1rem', // most of the design icons don't shrink with font size
        },
        label: {
          paddingLeft: 0,
          paddingRight: 0,
        },
        sizeSmall: {
          fontSize: '0.75rem',
          margin: '0.5rem 0',
        },
        sizeMedium: {
          fontSize: '1rem',
        },
      },
      variants: [
        {
          props: { variant: 'subtle' },
          style: {
            fontWeight: 400,
            color: 'inherit',
            backgroundColor: 'inherit',
            '& .MuiChip-icon': {
              color: 'inherit',
              marginLeft: 0,
            },
          },
        },
        {
          props: { variant: 'subtle', color: 'primary' },
          style: { '& .MuiChip-icon': { color: colours.brick } },
        },
        {
          props: { variant: 'subtle', color: 'info' },
          style: { '& .MuiChip-icon': { color: colours.fadedGrey } },
        },
        {
          props: { variant: 'filled' },
          style: { padding: '0 0.25rem' },
        },
        {
          props: { variant: 'inverted' },
          style: { backgroundColor: 'white' },
        },
        {
          props: { variant: 'inverted', color: 'primary' },
          style: { color: colours.brick },
        },
        {
          props: { variant: 'inverted', size: 'small' },
          style: {
            padding: '0rem 0.1875rem',
            borderRadius: '0.125rem',
          },
        },
      ],
    },
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: colours.lightGrey,
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'none',
      },
      variants: [
        {
          props: { variant: 'anchor' },
          style: {
            color: '#1270B8',
            fontSize: '1rem',
            fontWeight: 500,
            lineHeight: 1.5,
          },
        },
      ],
    },
    MuiPaper: { styleOverrides: { root: { color: '#1F1F1F' } } },
    MuiTooltip: {
      styleOverrides: { tooltip: { fontSize: ' 0.875rem' } },
    },
    MuiTypography: {
      // annoyingly, the font setting above ain't enough; @see:
      defaultProps: {
        // https://github.com/mui/material-ui/issues/28633
        fontFamily: font,
      },
      variants: [
        {
          props: { variant: 'visuallyHidden' },
          style: {
            clip: 'rect(0 0 0 0)',
            clipPath: 'inset(50%)',
            height: '1px',
            overflow: 'hidden',
            position: 'absolute',
            whiteSpace: 'nowrap',
            width: '1px',
          },
        },
      ],
    },
  },
})

theme.typography.h2[theme.breakpoints.up('sm')] = {
  fontSize: '2rem',
  lineHeight: 1.625,
}
theme.typography.subtitle1[theme.breakpoints.up('sm')] = {
  fontSize: '1.5rem',
  lineHeight: 32 / 24,
}

export default theme
